import { useEffect, useRef, useState, useCallback } from 'react'
import {Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Heading2, Heading3, Eraser, Code, Code2} from 'lucide-react'

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
}

/**
 * Editeur de texte enrichi minimal et stable basé sur contentEditable + document.execCommand.
 * - Évite les problèmes de re-render qui réinitialisent la position du curseur en ne mettant à jour
 *   le DOM interne que lorsque la value externe change ET diffère du HTML courant de l'élément.
 * - Mode HTML : permet à l'admin de basculer vers une vue "code" pour éditer directement
 *   le HTML brut de la section (utile pour insérer des balises non disponibles dans la toolbar,
 *   nettoyer du contenu collé, ou ajouter des classes / attributs).
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Saisissez votre texte…',
  minHeight = 160,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isHtmlMode, setIsHtmlMode] = useState(false)
  const [htmlDraft, setHtmlDraft] = useState<string>(value || '')

  // Synchronise la valeur externe -> DOM uniquement si différente,
  // pour éviter de casser le curseur pendant la frappe.
  useEffect(() => {
    if (isHtmlMode) return
    const el = editorRef.current
    if (!el) return
    if (el.innerHTML !== value) {
      el.innerHTML = value || ''
    }
  }, [value, isHtmlMode])

  // Quand on entre en mode HTML, on initialise le brouillon avec la dernière valeur connue.
  useEffect(() => {
    if (isHtmlMode) {
      setHtmlDraft(value || '')
    }
  }, [isHtmlMode, value])

  const exec = useCallback(
    (command: string, arg?: string) => {
      const el = editorRef.current
      if (!el) return
      el.focus()
      try {
        document.execCommand(command, false, arg)
      } catch (err) {
        console.warn('[RichTextEditor] exec failed:', command, err)
      }
      onChange(el.innerHTML)
    },
    [onChange],
  )

  const handleInput = () => {
    const el = editorRef.current
    if (!el) return
    onChange(el.innerHTML)
  }

  const handleLink = () => {
    const url = window.prompt('URL du lien (https://…) :', 'https://')
    if (!url) return
    exec('createLink', url)
  }

  const handleClearFormat = () => {
    exec('removeFormat')
    exec('formatBlock', 'p')
  }

  const toggleHtmlMode = () => {
    if (!isHtmlMode) {
      // visuel -> HTML : on capture la valeur actuelle de l'éditeur visuel
      const el = editorRef.current
      const current = el ? el.innerHTML : value || ''
      setHtmlDraft(current)
      setIsHtmlMode(true)
    } else {
      // HTML -> visuel : on commit le brouillon HTML comme valeur définitive
      onChange(htmlDraft)
      setIsHtmlMode(false)
    }
  }

  const handleHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value
    setHtmlDraft(next)
    onChange(next)
  }

  const isEmpty = !value || value === '<br>' || value === '<p></p>' || value === ''

  return (
    <div
      className={`border rounded-lg overflow-hidden bg-white transition-colors ${
        isFocused ? 'border-gray-900 ring-2 ring-gray-900/10' : 'border-gray-300'
      }`}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
        <ToolbarButton
          onClick={() => exec('bold')}
          title="Gras (Ctrl+B)"
          disabled={isHtmlMode}
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec('italic')}
          title="Italique (Ctrl+I)"
          disabled={isHtmlMode}
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec('underline')}
          title="Souligné (Ctrl+U)"
          disabled={isHtmlMode}
        >
          <Underline className="w-3.5 h-3.5" />
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton
          onClick={() => exec('formatBlock', 'h2')}
          title="Titre H2"
          disabled={isHtmlMode}
        >
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec('formatBlock', 'h3')}
          title="Titre H3"
          disabled={isHtmlMode}
        >
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec('formatBlock', 'p')}
          title="Paragraphe"
          disabled={isHtmlMode}
        >
          <span className="text-[10px] font-bold">P</span>
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton
          onClick={() => exec('insertUnorderedList')}
          title="Liste à puces"
          disabled={isHtmlMode}
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec('insertOrderedList')}
          title="Liste numérotée"
          disabled={isHtmlMode}
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton onClick={handleLink} title="Insérer un lien" disabled={isHtmlMode}>
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec('formatBlock', 'blockquote')}
          title="Citation"
          disabled={isHtmlMode}
        >
          <Code className="w-3.5 h-3.5" />
        </ToolbarButton>
        <span className="w-px h-5 bg-gray-300 mx-1" />
        <ToolbarButton
          onClick={handleClearFormat}
          title="Effacer le formatage"
          disabled={isHtmlMode}
        >
          <Eraser className="w-3.5 h-3.5" />
        </ToolbarButton>

        {/* Toggle HTML / Visuel à l'extrême droite */}
        <span className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggleHtmlMode}
            title={isHtmlMode ? 'Revenir à l\'éditeur visuel' : 'Éditer le code HTML'}
            className={`inline-flex items-center gap-1 px-2 h-7 rounded text-[11px] font-semibold transition-colors ${
              isHtmlMode
                ? 'bg-gray-900 text-white hover:bg-gray-800'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
            }`}
            aria-pressed={isHtmlMode}
          >
            <Code2 className="w-3.5 h-3.5" />
            {isHtmlMode ? 'Visuel' : 'HTML'}
          </button>
        </span>
      </div>

      {/* Editor */}
      <div className="relative">
        {!isHtmlMode ? (
          <>
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="prose-rte px-4 py-3 outline-none text-[14px] text-gray-900 leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-bold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_a]:text-blue-600 [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-gray-600"
              style={{ minHeight }}
              aria-label="Editeur de texte enrichi"
            />
            {isEmpty && !isFocused && (
              <span className="absolute top-3 left-4 text-gray-400 text-[14px] pointer-events-none">
                {placeholder}
              </span>
            )}
          </>
        ) : (
          <textarea
            value={htmlDraft}
            onChange={handleHtmlChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            spellCheck={false}
            placeholder="<p>Saisissez ou collez votre HTML ici…</p>"
            className="w-full px-4 py-3 outline-none text-[13px] font-mono text-gray-900 leading-relaxed bg-gray-50 resize-y"
            style={{ minHeight }}
            aria-label="Editeur de code HTML"
          />
        )}
      </div>

      {isHtmlMode && (
        <div className="px-3 py-2 border-t border-gray-200 bg-amber-50 text-[11px] text-amber-800">
          Mode HTML actif : vous éditez directement le code source. Cliquez sur « Visuel » pour
          revenir à l'éditeur enrichi.
        </div>
      )}
    </div>
  )
}

function ToolbarButton({
  children,
  onClick,
  title,
  disabled,
}: {
  children: React.ReactNode
  onClick: () => void
  title: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-7 h-7 rounded transition-colors ${
        disabled
          ? 'text-gray-300 cursor-not-allowed'
          : 'text-gray-700 hover:bg-white hover:text-gray-900 hover:shadow-sm'
      }`}
    >
      {children}
    </button>
  )
}
